/**
 * @file Parses a stored `parameters/analyticsBindings` document into the bindings it accepts and
 * the roles it rejects, each with a reason. A document malformed as a whole throws
 * `AnalyticsBindingsMalformed` with the offending key; a bad binding drops only its role. Roles are
 * visited in `ROLE_COLUMN_TYPE` key order, so the output never depends on input order. A table
 * reaches the bindings only through `isArRawTableId`, the full identifier gate. It is pure and lives
 * in `types/src`, so the fact build, serving, the triggers and the Settings binding editor all parse
 * with the same code.
 */
import type { ColumnRoleId } from './Analytics.Catalog.types';
import { ANALYTICS_BINDINGS_PATH } from './Analytics.Bindings.types';
import type {
    AnalyticsBindingRejection,
    AnalyticsBindings,
    AnalyticsRoleBinding,
    ParsedAnalyticsBindings,
} from './Analytics.Bindings.types';
import { ROLE_COLUMN_TYPE } from './Analytics.RawLayer.types';
import { AR_BUNDLE_ROLES } from './System.Artifacts.types';
import { AnalyticsBindingsMalformed } from './AnalyticsBindingsMalformed';
import { isArRawTableId } from './isArRawTableId';

type Fields = Readonly<Record<string, unknown>>;

type WeightedRoleBinding = NonNullable<AnalyticsBindings['roles']['salespersonMemberId']>;

type UnweightedRoleId = Exclude<ColumnRoleId, 'salespersonMemberId'>;

type ParsedRole =
    | { role: 'salespersonMemberId'; binding: WeightedRoleBinding }
    | { role: UnweightedRoleId; binding: AnalyticsRoleBinding }
    | AnalyticsBindingRejection;

const DOCUMENT_KEYS: readonly string[] = [
    'roles',
    'valueBundleRole',
    'projectReferenceKey',
    'ownerFieldId',
] satisfies (keyof AnalyticsBindings)[];
const BINDING_KEYS: readonly string[] = [
    'table',
    'typeId',
    'fields',
    'weighted',
] satisfies (keyof WeightedRoleBinding)[];
const WEIGHTED_ROLE = 'salespersonMemberId' satisfies ColumnRoleId;

const NOT_A_BINDING = 'binding is not an object of table, typeId, fields and weighted';
const TYPE_ID_NOT_TEXT = 'typeId is not a non-empty string';
const TABLE_MISSING = 'table is missing';
const TABLE_NOT_AN_AR_TABLE = 'table is not an ArTable collection id';
const FIELDS_NOT_A_PATH = 'fields is not a non-empty list of non-empty strings';
const WEIGHTED_NOT_A_FLAG = 'weighted is missing or not a boolean';
const WEIGHTED_ELSEWHERE = `weighted applies to ${WEIGHTED_ROLE} only`;

const isText = (value: unknown): value is string => typeof value === 'string' && value !== '';

const isFieldPath = (value: unknown): value is string[] =>
    Array.isArray(value) && value.length > 0 && value.every(isText);

const isColumnRoleId = (key: string): key is ColumnRoleId => Object.keys(ROLE_COLUMN_TYPE).includes(key);

const ROLE_IDS = Object.keys(ROLE_COLUMN_TYPE).filter(isColumnRoleId);

const plainFieldsOf = (value: unknown): Fields | undefined =>
    typeof value === 'object' && value !== null && [Object.prototype, null].includes(Object.getPrototypeOf(value))
        ? Object.fromEntries(Object.entries(value))
        : undefined;

const optionalTextOf = (document: Fields, key: 'projectReferenceKey' | 'ownerFieldId'): string | undefined => {
    const value = document[key];
    if (value !== undefined && !isText(value)) throw new AnalyticsBindingsMalformed(key);
    return value;
};

const parseRole = (role: ColumnRoleId, value: unknown): ParsedRole => {
    const entry = plainFieldsOf(value);
    if (entry === undefined || Object.keys(entry).some((key) => !BINDING_KEYS.includes(key))) {
        return { role, reason: NOT_A_BINDING };
    }
    const { table, typeId, fields, weighted } = entry;
    if (table === undefined) return { role, reason: TABLE_MISSING };
    if (!isArRawTableId(table)) return { role, reason: TABLE_NOT_AN_AR_TABLE };
    if (!isText(typeId)) return { role, reason: TYPE_ID_NOT_TEXT };
    if (fields !== undefined && !isFieldPath(fields)) return { role, reason: FIELDS_NOT_A_PATH };
    const binding = { table, typeId, ...(fields === undefined ? {} : { fields }) };
    if (role !== WEIGHTED_ROLE) {
        return weighted === undefined ? { role, binding } : { role, reason: WEIGHTED_ELSEWHERE };
    }
    return typeof weighted === 'boolean'
        ? { role, binding: { ...binding, weighted } }
        : { role, reason: WEIGHTED_NOT_A_FLAG };
};

const rolesOf = (parsed: readonly ParsedRole[]): AnalyticsBindings['roles'] =>
    parsed.reduce<AnalyticsBindings['roles']>(
        (roles, entry) => ('binding' in entry ? { ...roles, [entry.role]: entry.binding } : roles),
        {},
    );

export function parseAnalyticsBindings(data: unknown): ParsedAnalyticsBindings {
    const document = plainFieldsOf(data);
    if (document === undefined) throw new AnalyticsBindingsMalformed(ANALYTICS_BINDINGS_PATH);
    const unknownKey = Object.keys(document).find((key) => !DOCUMENT_KEYS.includes(key));
    if (unknownKey !== undefined) throw new AnalyticsBindingsMalformed(unknownKey);
    const roles = document.roles === undefined ? {} : plainFieldsOf(document.roles);
    if (roles === undefined) throw new AnalyticsBindingsMalformed('roles');
    const projectReferenceKey = optionalTextOf(document, 'projectReferenceKey');
    const ownerFieldId = optionalTextOf(document, 'ownerFieldId');
    const valueBundleRole = AR_BUNDLE_ROLES.find((bundleRole) => bundleRole === document.valueBundleRole);
    if (document.valueBundleRole !== undefined && valueBundleRole === undefined) {
        throw new AnalyticsBindingsMalformed('valueBundleRole');
    }
    const unknownRole = Object.keys(roles).find((key) => !isColumnRoleId(key));
    if (unknownRole !== undefined) throw new AnalyticsBindingsMalformed(`roles.${unknownRole}`);
    const parsed = ROLE_IDS.filter((role) => roles[role] !== undefined).map((role) => parseRole(role, roles[role]));
    return {
        bindings: {
            roles: rolesOf(parsed),
            ...(valueBundleRole === undefined ? {} : { valueBundleRole }),
            ...(projectReferenceKey === undefined ? {} : { projectReferenceKey }),
            ...(ownerFieldId === undefined ? {} : { ownerFieldId }),
        },
        rejected: parsed.flatMap((entry) => ('reason' in entry ? [entry] : [])),
    };
}
